const srgbChannelToLinear = (value) => {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

export const rgbToLab = ({ r, g, b }) => {
  const red = srgbChannelToLinear(r);
  const green = srgbChannelToLinear(g);
  const blue = srgbChannelToLinear(b);
  const x = (red * 0.4124 + green * 0.3576 + blue * 0.1805) / 0.95047;
  const y = red * 0.2126 + green * 0.7152 + blue * 0.0722;
  const z = (red * 0.0193 + green * 0.1192 + blue * 0.9505) / 1.08883;
  const convert = (value) => value > 0.008856 ? value ** (1 / 3) : 7.787 * value + 16 / 116;
  const fx = convert(x);
  const fy = convert(y);
  const fz = convert(z);
  return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
};

export const labDistance = (first, second) => Math.hypot(
  first.l - second.l,
  first.a - second.a,
  first.b - second.b,
);

const TEXTURE_WEIGHTS = {
  contrast: 26,
  edgeDensity: 32,
  darkCoverage: 24,
};

export const countSampledTerrainTypes = (samples) => new Set(
  samples.map((sample) => sample.terrainId),
).size;

export const terrainDescriptorDistance = (color, sample) => {
  const colorDistance = labDistance(rgbToLab(color), rgbToLab(sample.color));
  const textureDeltas = Object.entries(TEXTURE_WEIGHTS).map(([feature, weight]) => {
    const colorValue = color.features?.[feature];
    const sampleValue = sample.features?.[feature];
    if (!Number.isFinite(colorValue) || !Number.isFinite(sampleValue)) return 0;
    return (colorValue - sampleValue) * weight;
  });
  return Math.hypot(colorDistance, ...textureDeltas);
};

export const classifyTerrainColors = (colors, samples, previousAssignments = {}) => {
  const assignments = Object.fromEntries(
    Object.entries(previousAssignments).filter(([, assignment]) => assignment.source === 'manual'),
  );
  if (countSampledTerrainTypes(samples) < 2) return assignments;

  Object.entries(colors).forEach(([key, color]) => {
    if (previousAssignments[key]?.source === 'manual') {
      assignments[key] = previousAssignments[key];
      return;
    }
    const ranked = samples
      .map((sample) => ({ sample, distance: terrainDescriptorDistance(color, sample) }))
      .sort((first, second) => first.distance - second.distance);
    const nearest = ranked[0];
    const alternative = ranked.find((candidate) => candidate.sample.terrainId !== nearest.sample.terrainId);
    const absoluteScore = Math.max(0, 1 - nearest.distance / 60);
    const separationScore = alternative
      ? Math.max(0, Math.min(1, (alternative.distance - nearest.distance) / 30))
      : absoluteScore;
    assignments[key] = {
      terrainId: nearest.sample.terrainId,
      confidence: Number((absoluteScore * 0.65 + separationScore * 0.35).toFixed(3)),
      source: 'auto',
    };
  });
  return assignments;
};

export const isAmbiguousAssignment = (assignment) => (
  assignment?.source === 'auto' && assignment.confidence < 0.42
);
